import React from "react";
import Markdown from "markdown-to-jsx";

const elements = [
  "a",
  "abbr",
  "address",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "base",
  "bdi",
  "bdo",
  "big",
  "blockquote",
  "body",
  "br",
  "button",
  "canvas",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "data",
  "datalist",
  "dd",
  "del",
  "details",
  "dfn",
  "dialog",
  "div",
  "dl",
  "dt",
  "em",
  "embed",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hgroup",
  "hr",
  "html",
  "i",
  "iframe",
  "img",
  "input",
  "ins",
  "kbd",
  "keygen",
  "label",
  "legend",
  "li",
  "link",
  "main",
  "map",
  "mark",
  "marquee",
  "menu",
  "menuitem",
  "meta",
  "meter",
  "nav",
  "noscript",
  "object",
  "ol",
  "optgroup",
  "option",
  "output",
  "p",
  "param",
  "picture",
  "pre",
  "progress",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "script",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "title",
  "tr",
  "track",
  "u",
  "ul",
  "var",
  "video",
  "wbr",

  // SVG
  "circle",
  "clipPath",
  "defs",
  "ellipse",
  "foreignObject",
  "g",
  "image",
  "line",
  "linearGradient",
  "marker",
  "mask",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialGradient",
  "rect",
  "stop",
  "svg",
  "text",
  "tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

// FIX 10/06 #140: detectar CPF/CNPJ/AnyDesk no body e renderizar botões de copiar.
// Validador de CPF (dígito verificador) — evita false positive com telefones.
function isValidCPF(str) {
  const cpf = String(str || "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i], 10) * (10 - i);
  let d1 = (sum * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== parseInt(cpf[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i], 10) * (11 - i);
  let d2 = (sum * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === parseInt(cpf[10], 10);
}

function isValidCNPJ(str) {
  const cnpj = String(str || "").replace(/\D/g, "");
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;
  const calc = (size) => {
    let sum = 0;
    let pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(cnpj[size - i], 10) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return (
    calc(12) === parseInt(cnpj[12], 10) &&
    calc(13) === parseInt(cnpj[13], 10)
  );
}

const COPY_MATCHERS = [
  {
    // CPF formatado XXX.XXX.XXX-XX OU 11 dígitos puros + DV check.
    // FIX 12/06: cru aceita se DV valido E nao bater formato celular BR
    // (DDD + 9 + 8 digitos). Tira false positive de telefone mas aceita CPFs
    // que comecem com 0 ou DDD invalido (00096721090, etc).
    label: "CPF",
    regex: /\b(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11})\b/g,
    extract: (match, fullText) => {
      const m = match[0];
      const isFormatted = m.includes(".");
      const digits = m.replace(/\D/g, "");
      if (!isValidCPF(digits)) return null;
      if (isFormatted) return m;
      // padrao celular BR: DDD valido (1-9 + 1-9) + 9 + 8 digitos
      if (/^[1-9][1-9]9\d{8}$/.test(digits)) {
        const ctx = String(fullText || "").toLowerCase();
        // so aceita se texto explicitar 'cpf' (ex: 'cpf 51993807020')
        return /\bcpf\b/.test(ctx) ? m : null;
      }
      return m;
    }
  },
  {
    // CNPJ formatado XX.XXX.XXX/XXXX-XX OU 14 dígitos puros + DV check.
    // Puros exigem palavra "cnpj" no texto pra evitar falsos.
    label: "CNPJ",
    regex: /\b(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}|\d{14})\b/g,
    extract: (match, fullText) => {
      const m = match[0];
      const isFormatted = m.includes(".") || m.includes("/");
      const digits = m.replace(/\D/g, "");
      if (!isValidCNPJ(digits)) return null;
      if (isFormatted) return m;
      const ctx = String(fullText || "").toLowerCase();
      if (/\bcnpj\b/.test(ctx)) return m;
      return null;
    }
  },
  {
    label: "AnyDesk",
    regex: /\bany\s?desk[:\s\-]*(\d{9,10})\b/gi,
    extract: (match) => {
      const m = match[0];
      const digits = m.replace(/[^\d]/g, "");
      return digits.length >= 9 ? digits : null;
    }
  },
  {
    label: "Email",
    regex: /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g,
    extract: (match) => match[0]
  },
  {
    // FIX 12/06 v7: fallback - qualquer sequencia 4+ digitos vira label "Numero"
    // se nao foi capturado por CPF/CNPJ/AnyDesk acima (deduplicacao no extract).
    label: "Número",
    regex: /\b(\d[\d\.\-/\s]{3,}\d)\b/g,
    extract: (match) => {
      const m = match[0].trim();
      const digits = m.replace(/\D/g, "");
      if (digits.length < 4) return null;
      return m;
    }
  }
];

function extractCopyMatches(text) {
  if (typeof text !== "string" || !text) return [];
  const found = [];
  const seenKeys = new Set();
  const seenDigits = new Set();
  COPY_MATCHERS.forEach(({ label, regex, extract }) => {
    const r = new RegExp(regex.source, regex.flags);
    let m;
    while ((m = r.exec(text)) !== null) {
      const value = extract(m, text);
      if (!value) continue;
      const key = `${label}:${value}`;
      if (seenKeys.has(key)) continue;
      // FIX 12/06 v7: se valor (so digitos) ja foi pego por label especifico antes,
      // nao adicionar como Numero generico.
      const digitsOnly = String(value).replace(/\D/g, "");
      if (label === "Número" && digitsOnly && seenDigits.has(digitsOnly)) continue;
      seenKeys.add(key);
      if (digitsOnly && digitsOnly.length >= 4) seenDigits.add(digitsOnly);
      found.push({ label, value });
    }
  });
  return found;
}

const CopyChip = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };
  // v5: botaozinho discreto - somente icone 14px com tooltip; sem label visivel.
  // Aparece inline ao lado da mensagem ao inves de bloco abaixo.
  return (
    <button
      type="button"
      onClick={handle}
      title={copied ? 'Copiado!' : `Copiar ${label}: ${value}`}
      aria-label={`Copiar ${label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        marginLeft: 4,
        padding: 0,
        fontSize: 11,
        lineHeight: 1,
        borderRadius: 3,
        border: "none",
        background: copied ? "#c8e6c9" : "transparent",
        color: copied ? "#2e7d32" : "#777",
        cursor: "pointer",
        userSelect: "none",
        opacity: 0.65,
        transition: "opacity 120ms, background 120ms"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.65'; e.currentTarget.style.background = copied ? '#c8e6c9' : 'transparent'; }}
    >
      {copied ? '✓' : '📋'}
    </button>
  );
};

const MarkdownWrapper = ({ children, enableCopy = false }) => {
  const boldRegex = /\*(.*?)\*/g;
  const tildaRegex = /~(.*?)~/g;

  const pixMarker = "###PIX_MARKER###";

  if (children && children.includes("***")) {
    children = children.replace(/\*\*\*/g, pixMarker);
  }

  if (children && boldRegex.test(children)) {
    children = children.replace(boldRegex, "**$1**");
  }

  if (children && children.includes(pixMarker)) {
    children = children.replace(new RegExp(pixMarker, "g"), "***");
  }

  if (children && tildaRegex.test(children)) {
    children = children.replace(tildaRegex, "~~$1~~");
  }
  const options = React.useMemo(() => {
    const markdownOptions = {
      disableParsingRawHTML: true,
      forceInline: true,
      overrides: {
        a: { component: CustomLink },
      },
    };

    elements.forEach((element) => {
      if (!allowedElements.includes(element)) {
        markdownOptions.overrides[element] = (el) => el.children || null;
      }
    });

    return markdownOptions;
  }, []);

  if (!children) return null;

  // FIX 12/06 v6: MarkdownWrapper nao renderiza mais CopyChip inline.
  // Botao de copiar fica FORA do balao, ao lado do icone reacao
  // (MessagesList importa extractCopyMatches e renderiza um botao externo).
  return <Markdown options={options}>{children}</Markdown>;
};

export default MarkdownWrapper;
export { extractCopyMatches };
